// src/utils/encryptionUtils.js
import { devLog } from '../App';

const encoder = new TextEncoder();
const decoder = new TextDecoder();
const IV_LENGTH = 12;
const ITERATIONS = 100_000;
const APP_SALT = 'based-app-salt-123'; // Change this for your app

function getCryptoSubtleOrThrow() {
    // CRA ESLint restricts `self`; this bundle runs in the browser, so `window` is enough.
    const root = typeof window !== 'undefined' ? window : {};

    const subtle = root?.crypto?.subtle;
    if (subtle) return subtle;

    const origin = (() => {
        try {
            return root?.location?.origin;
        } catch {
            return undefined;
        }
    })();

    const isSecureContext = Boolean(root?.isSecureContext);

    // This is the common case on iOS Safari when running from http://<LAN-IP>
    const msg =
        `Encryption is unavailable because WebCrypto (crypto.subtle) is not available in this browser context.` +
        (origin ? ` Origin: ${origin}.` : '') +
        ` This typically happens when the app is loaded over HTTP instead of HTTPS.` +
        (isSecureContext ? '' : ` (Not a secure context.)`) +
        ` Use HTTPS (or localhost) and try again.`;

    const err = new Error(msg);
    err.name = 'WebCryptoUnavailableError';
    throw err;
}

export async function deriveWorkspaceKey(workspaceId) {
    const subtle = getCryptoSubtleOrThrow();
    const workspaceCredential = `${workspaceId}-workspace-salt`;
    const password = `${workspaceCredential}-based-saas`;
    const salt = encoder.encode(APP_SALT);

    const baseKey = await subtle.importKey(
        'raw',
        encoder.encode(password),
        'PBKDF2',
        false,
        ['deriveKey']
    );

    return subtle.deriveKey(
        {
            name: 'PBKDF2',
            salt,
            iterations: ITERATIONS,
            hash: 'SHA-256'
        },
        baseKey,
        {
            name: 'AES-GCM',
            length: 256
        },
        false,
        ['encrypt', 'decrypt']
    );
}

export async function deriveKeyFromCredential(credential) {
    const subtle = getCryptoSubtleOrThrow();
    const password = `${credential}-based-saas`;
    const salt = encoder.encode(APP_SALT);

    const baseKey = await subtle.importKey(
        'raw',
        encoder.encode(password),
        'PBKDF2',
        false,
        ['deriveKey']
    );

    return subtle.deriveKey(
        {
            name: 'PBKDF2',
            salt,
            iterations: ITERATIONS,
            hash: 'SHA-256'
        },
        baseKey,
        {
            name: 'AES-GCM',
            length: 256
        },
        false,
        ['encrypt', 'decrypt']
    );
}

export async function encryptText(text, key) {
    const subtle = getCryptoSubtleOrThrow();
    const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
    const encrypted = await subtle.encrypt(
        { name: 'AES-GCM', iv },
        key,
        encoder.encode(text)
    );

    const combined = new Uint8Array(iv.length + encrypted.byteLength);
    combined.set(iv);
    combined.set(new Uint8Array(encrypted), iv.length);

    // Convert Uint8Array to binary string
    const binaryString = Array.from(combined).map(byte => String.fromCharCode(byte)).join('');
    return btoa(binaryString);
}

export async function decryptText(base64, key) {
    try {
        const subtle = getCryptoSubtleOrThrow();
        devLog("🔍 Attempting to decrypt:", {
            base64Data: base64,
            dataType: typeof base64,
            isArray: Array.isArray(base64),
            dataLength: base64?.length,
            firstItem: Array.isArray(base64) ? base64[0] : null
        });

        // Handle array case - this is likely already decrypted data
        if (Array.isArray(base64)) {
            devLog("⚠️ Received array instead of encrypted string - returning as-is");
            return base64;
        }

        // Check if data exists and is a string
        if (!base64 || typeof base64 !== 'string') {
            console.error("❌ Invalid encrypted data:", base64);
            return null;
        }

        // Check if it looks like valid Base64
        const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
        if (!base64Regex.test(base64)) {
            console.error("❌ Data doesn't look like valid Base64:", base64);
            return null;
        }

        // Try the atob() call
        devLog("✅ About to call atob()...");
        const combined = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
        devLog("✅ Successfully decoded Base64");

        const iv = combined.slice(0, IV_LENGTH);
        const data = combined.slice(IV_LENGTH);

        const decrypted = await subtle.decrypt(
            { name: 'AES-GCM', iv },
            key,
            data
        );

        const result = decoder.decode(decrypted);
        devLog("✅ Successfully decrypted text", result);
    
        return result;

    } catch (error) {
        console.error("❌ Decryption failed:", error);
        console.error("📄 Failed data:", base64);

        // If it's a Base64 error, try to clear corrupted data
        if (error.name === 'InvalidCharacterError') {
            devLog("🧹 Clearing potentially corrupted localStorage data");
            localStorage.removeItem('encryptedUserProfile');
            localStorage.removeItem('userCredential');
        }

        return null;
    }
}

/**
 * Heuristic: value is likely encryptText() output stored in Firestore (base64 IV + ciphertext).
 * Excludes typical plaintext names (spaces) and emails (@).
 * @param {string} value
 * @returns {boolean}
 */
export function looksLikeEncryptedUserField(value) {
    if (!value || typeof value !== 'string') return false;
    const base64Pattern = /^[A-Za-z0-9+/=]+$/;
    return (
        value.length > 30 &&
        base64Pattern.test(value) &&
        !value.includes('@') &&
        !value.includes(' ')
    );
}

/**
 * Unwrap a decrypted value that may have been stored as JSON.stringify(string)
 * (e.g. legacy signup stored name as encrypt(JSON.stringify("Gary Riger"))).
 * If the result is a JSON-stringified string, returns the inner string; otherwise returns as-is.
 * @param {*} str - Decrypted value (usually a string)
 * @returns {*} The unwrapped string, or original value
 */
export function unwrapJsonString(str) {
    if (str == null || typeof str !== 'string') {
        return str;
    }
    try {
        const parsed = JSON.parse(str);
        if (typeof parsed === 'string') {
            return parsed;
        }
    } catch (_) {
        // not valid JSON or not a string value
    }
    return str;
}

export async function createDualEncryption(content, userUid, workspaceIds) {
    if (!content || !userUid || !Array.isArray(workspaceIds)) {
        throw new Error("Missing required parameters for dual encryption");
    }

    try {
        devLog("🔐 Creating dual encryption for workspaces:", workspaceIds);
        
        // User encryption (existing logic)
        const userKey = await deriveKeyFromCredential(userUid);
        const userEncrypted = await encryptText(content, userKey);

        // Workspace encryption for each workspace
        const workspaceEncryptedResults = {};
        for (const workspaceId of workspaceIds) {
            devLog(`🔐 Encrypting for workspace: ${workspaceId}`);
            const workspaceKey = await deriveWorkspaceKey(workspaceId);
            workspaceEncryptedResults[workspaceId] = await encryptText(content, workspaceKey);
        }

        devLog("✅ Dual encryption completed successfully");
        
        return {
            userEncryptedResult: userEncrypted,
            workspaceEncryptedResults: workspaceEncryptedResults,
            isEncrypted: true,
            encryptionMethod: "dual"
        };
    } catch (error) {
        console.error("❌ Dual encryption failed:", error);
        throw error;
    }
}

/**
 * User-key-only encryption (no workspace blobs). Used for Env Vault so
 * collaborators / server RAG paths cannot decrypt via workspace keys.
 * @param {string} content
 * @param {string} userUid
 */
export async function createUserOnlyEncryption(content, userUid) {
    if (!content || !userUid) {
        throw new Error('Missing required parameters for user-only encryption');
    }
    const userKey = await deriveKeyFromCredential(userUid);
    const userEncrypted = await encryptText(content, userKey);
    return {
        userEncryptedResult: userEncrypted,
        isEncrypted: true,
        encryptionMethod: 'user',
    };
}

export async function decryptWithWorkspaceKey(workspaceEncryptedResults, workspaceId) {
    if (!workspaceEncryptedResults || !workspaceId) {
        return null;
    }

    const encryptedData = workspaceEncryptedResults[workspaceId];
    if (!encryptedData) {
        console.warn(`No encryption found for workspace: ${workspaceId}`);
        return null;
    }

    try {
        devLog(`🔓 Decrypting with workspace key for: ${workspaceId}`);
        const workspaceKey = await deriveWorkspaceKey(workspaceId);
        const result = await decryptText(encryptedData, workspaceKey);
        
        if (result) {
            devLog(`✅ Successfully decrypted with workspace key: ${workspaceId}`);
        } else {
            devLog(`❌ Failed to decrypt with workspace key: ${workspaceId}`);
        }
        
        return result;
    } catch (error) {
        console.error(`❌ Failed to decrypt with workspace key for ${workspaceId}:`, error);
        return null;
    }
}

export function canWorkspaceAccessDocument(doc, workspaceId) {
    if (!doc.isEncrypted) {
        return true; // Plain text documents are accessible
    }

    if (doc.encryptionMethod === "dual" && doc.workspaceEncryptedResults) {
        return Boolean(doc.workspaceEncryptedResults[workspaceId]);
    }

    return false; // Legacy encrypted documents are not accessible to workspace services
}

export async function migrateLegacyDocumentToDual(doc, userUid, workspaceIds) {
    if (!doc.isEncrypted || doc.encryptionMethod === "dual") {
        return doc; // Already dual or not encrypted
    }

    try {
        devLog("🔄 Migrating legacy document to dual encryption");
        
        // Try to decrypt the legacy document
        const userKey = await deriveKeyFromCredential(userUid);
        const decryptedContent = await decryptText(doc.encryptedResult, userKey);
        
        if (!decryptedContent) {
            console.error("Could not decrypt legacy document for migration");
            return doc;
        }

        // Create dual encryption
        const dualEncryption = await createDualEncryption(decryptedContent, userUid, workspaceIds);
        
        devLog("✅ Legacy document migrated successfully");
        
        return {
            ...doc,
            ...dualEncryption,
            // Remove legacy field
            encryptedResult: undefined
        };
    } catch (error) {
        console.error("❌ Migration failed:", error);
        return doc;
    }
}